/* global google */

import React, { Component } from 'react'
import { GoogleMap, MarkerClusterer } from '@react-google-maps/api'
import classnames from 'clsx'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Marker from './PropertyMarker'
import { getWidth } from 'lib/getViewport'
import arrayConcatDedupe from 'lib/array/arrayConcatDedupe'
import arraysEqual from 'lib/array/arraysEqual'

import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'

import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'

import { styles as clusterStyles } from 'components/Common/PropertyMap/Cluster'
import { fitBoundsWithExtraSpace } from 'components/Common/PropertyMap/utils'

// over 480 - the desktop version of the fitted map looks good, less than 480 - a lot of grey space on top and under the map
const MAX_MOBILE_WIDTH = 480

interface Props {
  className?: string;
  height?: string | number;
  markerOptions?: {
    xOffset: number;
    yOffset: number;
  };
  markers: Array<App.MapMarker>;
  multipleMarkers?: boolean;
  onSelectMarker?: (marker: App.MapMarker) => void;
  renderInfoWindow?: (item: App.MapMarker, onCloseClick: () => void) => React.ReactNode;
  onDrag?: () => void;
  mapOptions?: Record<string, unknown>
  wrapperStyle?: Record<string, unknown>
}

interface State {
  activeMarker: {
    [key: string]: any;
  };
  activeMarkerInfoWindowOpen: boolean;
}

class PropertyMap extends Component<React.PropsWithChildren<Props>, State> {
  map = null

  static defaultProps = {
    markerOptions: {
      xOffset: 0,
      yOffset: 0,
    },
  }

  constructor(props) {
    super(props)

    this.state = {
      activeMarker: {},
      activeMarkerInfoWindowOpen: false,
    }
  }

  toggleActiveMarkerInfoWindow = (): void => {
    this.setState(state => ({
      activeMarkerInfoWindowOpen: !state.activeMarkerInfoWindowOpen,
    }))
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Check for different props
    const propsKeys = arrayConcatDedupe(Object.keys(this.props), Object.keys(nextProps))
    for (const key of propsKeys) {
      if (this.props[key] !== nextProps[key]) {
        return true
      }
    }

    // Check for different state
    const stateKeys = arrayConcatDedupe(Object.keys(this.state), Object.keys(nextState))
    for (const key of stateKeys) {
      if (this.state[key] !== nextState[key]) {
        return true
      }
    }

    return false
  }

  componentDidUpdate(prevProps) {
    const { markers } = this.props
    if (
      !arraysEqual(prevProps.markers.map(p => p.id), markers.map(p => p.id), true) &&
        this.props.markers.length > 0 &&
        this.map
    ) {
      this.fitMarkersOnMap(markers)
    }
  }

  fitMarkersOnMap(markers: Array<App.MapMarker>) {
    if (!this.map || markers.length === 0) {
      return
    }

    if (markers.length === 1) {
      this.map.setCenter(new google.maps.LatLng(markers[0].latitude, markers[0].longitude))
      this.map.setZoom(15)
      return
    }

    const bounds = new google.maps.LatLngBounds()

    markers.forEach(p => {
      bounds.extend(new google.maps.LatLng(p.latitude, p.longitude))
    })

    const longitudeSpan = bounds.getNorthEast().lng() - bounds.getSouthWest().lng()

    if (getWidth() <= MAX_MOBILE_WIDTH && longitudeSpan > 100) {
      this.map.setCenter(new google.maps.LatLng(markers[0]?.latitude, markers[0]?.longitude))
      this.map.setZoom(1)
    } else {
      fitBoundsWithExtraSpace(this.map, bounds)
    }
  }

  focusMarker = (markerId: string): void => {
    const { markers, onSelectMarker } = this.props
    const marker = markers.find(x => x.id === markerId)

    if (marker) {
      this.setState({
        activeMarker: marker,
        activeMarkerInfoWindowOpen: true,
      })
      onSelectMarker && onSelectMarker(marker)
      this.panWithPixelOffset(new google.maps.LatLng(marker.latitude, marker.longitude), this.props.markerOptions.xOffset, this.props.markerOptions.yOffset)
    }
  }

  panWithPixelOffset = (latLng: google.maps.LatLng, xOffset: number = 0, yOffset: number = 0): void => {
    if (!this.map) {
      return
    }

    const proj = this.map.getProjection()
    const point = proj.fromLatLngToPoint(latLng)
    const scale = Math.pow(2, this.map.getZoom())
    const adjustedPoint = new google.maps.Point((point.x + xOffset / scale), (point.y + yOffset / scale))
    const adjustedLatLng = proj.fromPointToLatLng(adjustedPoint)
    this.map.panTo(adjustedLatLng)
  }

  setActiveMarker = (marker: App.MapMarker) => {
    this.focusMarker(marker.id)
  }

  shouldMarkerBeActive = (marker: App.MapMarker): boolean =>
    this.state.activeMarker.latitude === marker.latitude && this.state.activeMarker.longitude === marker.longitude

  onClusterClick = (cluster) => {
    // Override default click behaviour
    fitBoundsWithExtraSpace(this.map, cluster.getBounds())
  }

  render() {
    const {
      markers,
      multipleMarkers,
      className,
      height,
      mapOptions,
      onDrag,
      renderInfoWindow,
      wrapperStyle,
    } = this.props

    const {
      activeMarkerInfoWindowOpen,
    } = this.state

    const classes = classnames(
      'PropertyMap',
      className,
    )

    const groupMarkers = markers.reduce((acc, marker) => {
      if (marker.type === PROPERTY_MAP_MARKER_TYPE) {
        acc.white.push(marker)
      } else {
        acc.black.push(marker)
      }

      return acc
    }, {
      white: [],
      black: [],
    })

    return (
      <div className={classes} style={wrapperStyle}>
        <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
          <GoogleMap
            id="le-map"
            zoom={17}
            onDrag={onDrag}
            mapContainerStyle={{
              height,
              width: '100%',
              flexGrow: 1,
            }}
            options={mapOptions}
            onLoad={(map) => {
              if (map) {
                const firstLoad = !this.map
                this.map = map
                if (firstLoad && markers.length > 0) {
                  this.fitMarkersOnMap(markers)
                }
              }
            }}
            onUnmount={(map) => {
              if (map) {
                this.map = null
              }
            }}
          >
            {Object.keys(groupMarkers).map(type => <MarkerClusterer
              key={`${type}GroupMarkers`}
              averageCenter
              enableRetinaIcons
              gridSize={30}
              zoomOnClick={false}
              onClick={this.onClusterClick}
              styles={[
                clusterStyles[type],
                clusterStyles[type],
              ]}
            >
              {clusterer =>
                groupMarkers[type].map((marker) => {
                  const active = this.shouldMarkerBeActive(marker)
                  return <Marker
                    key={marker.id}
                    multipleMarkers={multipleMarkers}
                    marker={marker}
                    active={active}
                    setActiveMarker={this.setActiveMarker}
                    clusterer={clusterer}
                  >
                    {active && renderInfoWindow && activeMarkerInfoWindowOpen &&
                      renderInfoWindow(marker, this.toggleActiveMarkerInfoWindow)
                    }
                  </Marker>
                })
              }
            </MarkerClusterer>)}
          </GoogleMap>
        </EnsureGoogleMapsLoaded>
      </div>
    )
  }
}

export default PropertyMap
